import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Download, Sparkles } from 'lucide-react';
import { MOOD_IMAGES, MOOD_FALLBACK } from './MoodPicker';

const Stat = ({ value, label }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
    <p className="text-2xl font-semibold text-white leading-none">{value}</p>
    <p className="text-xs text-white/45 mt-1.5">{label}</p>
  </div>
);

const MoodSpread = ({ mood }) => {
  const total = mood.total || 0;
  return (
    <div className="space-y-2" data-testid="insights-mood-spread">
      {[5, 4, 3, 2, 1].map((value) => {
        const count = mood.distribution?.[value] || 0;
        const share = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={value} className="flex items-center gap-3" data-testid={`spread-${value}`}>
            <img src={MOOD_IMAGES[value]} alt="" loading="lazy"
                 className="w-10 h-10 rounded-lg object-cover border border-white/10 shrink-0" />
            <span className="w-40 sm:w-48 text-xs text-white/60 shrink-0">
              {MOOD_FALLBACK[value]}
            </span>
            <span className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
              <span className="block h-full rounded-full bg-white/35 transition-all duration-700"
                    style={{ width: `${share}%` }} />
            </span>
            <span className="w-14 text-right text-[11px] text-white/40 shrink-0">
              {count} · {share}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

const WritingRhythm = ({ writing }) => {
  const peak = Math.max(1, ...writing.by_month.map((m) => m.entries));
  return (
    <div className="flex items-end gap-3 h-28" data-testid="insights-rhythm">
      {writing.by_month.map((month) => (
        <div key={month.month}
             className="flex flex-col items-center gap-2 w-12 sm:w-14 shrink-0">
          <span className="w-full rounded-t-md bg-teal-300/30 transition-all duration-700"
                style={{ height: `${Math.max(4, Math.round((month.entries / peak) * 84))}px` }}
                title={`${month.entries} entries`} />
          <span className="text-[10px] text-white/35 truncate w-full text-center">
            {month.month.slice(5)}/{month.month.slice(2, 4)}
          </span>
        </div>
      ))}
    </div>
  );
};

const Keepsake = () => {
  const [parts, setParts] = useState({ journal: true, feedback: true, work: true });
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const query = new URLSearchParams({
        journal: parts.journal, feedback: parts.feedback, work: parts.work,
      });
      const res = await axios.get(`${API}/companion/keepsake?${query}`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(res.data);
      link.download = 'journey-keepsake.pdf';
      link.click();
      window.URL.revokeObjectURL(link.href);
      toast.success('Your keepsake is downloading');
    } catch {
      toast.error('The keepsake could not be made just now');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (key) => setParts({ ...parts, [key]: !parts[key] });

  return (
    <section className="rounded-2xl border border-amber-300/25 bg-amber-300/[0.06] p-6 space-y-4"
             data-testid="keepsake">
      <div>
        <h2 className="text-base md:text-lg font-semibold text-white flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-200" /> Your Journey Keepsake
        </h2>
        <p className="text-sm text-white/55 mt-1">
          Everything above, plus your values, your milestones and — if you want them — your own
          words, as a document you can print and keep.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          ['journal', 'Include my journal entries'],
          ['feedback', 'Include what my practitioners said'],
          ['work', 'Include the work I handed in'],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => toggle(key)}
            className={`rounded-full border px-4 py-1.5 text-xs transition-colors duration-200
                        ${parts[key] ? 'border-amber-300/50 bg-amber-300/20 text-amber-50'
                                     : 'border-white/10 bg-white/[0.03] text-white/45 hover:text-white/75'}`}
            data-testid={`keepsake-part-${key}`}
          >
            {parts[key] ? '✓ ' : ''}{label}
          </button>
        ))}
      </div>

      <button
        onClick={download}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-amber-300/20 border
                   border-amber-300/45 px-5 py-2.5 text-sm text-amber-50 hover:bg-amber-300/30
                   disabled:opacity-40 transition-colors duration-200"
        data-testid="keepsake-download"
      >
        <Download className="w-4 h-4" />
        {busy ? 'Making it…' : 'Download my keepsake'}
      </button>
    </section>
  );
};

/** What their own data quietly says — and the keepsake made from it. */
export const Insights = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/companion/insights`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  if (!data) {
    return <p className="text-sm text-white/50" data-testid="insights-loading">Reading your journey…</p>;
  }

  return (
    <div className="space-y-10" data-testid="insights">
      {data.notices?.length > 0 && (
        <section className="space-y-2" data-testid="insights-notices">
          <h2 className="text-base md:text-lg font-semibold text-white">What we notice</h2>
          <ul className="space-y-2">
            {data.notices.map((notice) => (
              <li key={notice}
                  className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3
                             text-sm text-white/80">
                {notice}
              </li>
            ))}
          </ul>
          <p className="text-xs text-white/35">
            Drawn from your own numbers — nothing here is interpretation you did not earn.
          </p>
        </section>
      )}

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="insights-stats">
        <Stat value={data.work.sections} label="sections completed" />
        <Stat value={data.work.handins} label="pieces handed in" />
        <Stat value={data.writing.entries} label="journal entries" />
        <Stat value={data.writing.words.toLocaleString()} label="words written" />
      </section>

      {data.mood.total > 0 && (
        <section className="space-y-3" data-testid="insights-mood">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-white">How you have been</h2>
            <p className="text-sm text-white/45">
              {data.mood.total} reading{data.mood.total === 1 ? '' : 's'}
              {data.mood.average ? ` · averaging ${data.mood.average} of 5` : ''}
            </p>
          </div>
          <MoodSpread mood={data.mood} />
          {data.mood.trend_sentence && (
            <p className="text-sm text-white/70 pt-1">{data.mood.trend_sentence}</p>
          )}
        </section>
      )}

      {data.writing.by_month.length > 0 && (
        <section className="space-y-3" data-testid="insights-writing">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-white">Your writing rhythm</h2>
            <p className="text-sm text-white/45">
              {data.writing.days} day{data.writing.days === 1 ? '' : 's'} written
              {data.writing.longest_run > 1
                ? ` · longest run ${data.writing.longest_run} days`
                : ''}
              {data.writing.busiest_weekday ? ` · most often on a ${data.writing.busiest_weekday}` : ''}
            </p>
          </div>
          <WritingRhythm writing={data.writing} />
        </section>
      )}

      <Keepsake />
    </div>
  );
};

export default Insights;
